import { BaseService } from './BaseService';
import {  PaginatedUsers, Enterprise, EnterpriseAdmin, UserListAdmin } from '../models';
import { createLogger } from './Debug';

export class AdminService extends BaseService {
  constructor() {

    super();
  }

  async getUsers(page = 1, limit = 20, search = '') {
    const log = createLogger('AdminService.getUsers');
    const params = new URLSearchParams();
    if (page) params.append('page', page);
    if (limit) params.append('limit', limit);
    if (search) params.append('search', search);
    const response = await this.get(`/users/admin/users?${params.toString()}`);
    const data = response.data || response;
    log.debug(data);
    return new PaginatedUsers(data);
  }

  async getUser(id) {
    const response = await this.get(`/users/admin/users/${id}`);
    return response.user ? new UserListAdmin(response.user) : null;
  }

  async updateUser(id, updates) {
    const response = await this.put(`/users/admin/users/${id}`, updates);
    return response.user ? new UserListAdmin(response.user) : null;
  }

  async deleteUser(id) {
    await this.delete(`/users/admin/users/${id}`);
  }

  async updateUserSubscription(userId, planType) {
    const response = await this.put(`/admin/users/${userId}/subscription`, { planType });
    return response.user ? new UserListAdmin(response.user) : null;
  }

  // Enterprise Management Methods
  
  /**
   * Set user admin status for an enterprise
   * @param {string} enterpriseId - Enterprise ID
   * @param {string} userId - User ID
   * @param {boolean} isAdmin - Admin status
   * @returns {Promise<Object>} Success response
   */
  async setUserAdminStatus(enterpriseId, userId, isAdmin) {
    const response = await this.put(`/enterprises/${enterpriseId}/users/${userId}/admin`, {
      isAdmin
    });
    return response;
  }

  /**
   * Get enterprise admins
   * @param {string} enterpriseId - Enterprise ID
   * @returns {Promise<Array>} Array of admin users
   */
  async getEnterpriseAdmins(enterpriseId) {
    const log = createLogger('AdminService.getEnterpriseAdmins');
    const response = await this.get(`/enterprises/${enterpriseId}/admins`);
    const admins = response.data?.admins || [];
    return admins.map(admin => new EnterpriseAdmin(admin));
  }

  /**
   * Create a new enterprise (System Admin Only)
   * @param {Object} enterpriseData - Enterprise data
   * @returns {Promise<Enterprise>} Created enterprise
   */
  async createEnterprise(enterpriseData) {
    const response = await this.post('/enterprises', enterpriseData);
    const enterprise = response.data?.enterprise || response.enterprise;
    return new Enterprise(enterprise);
  }

  /**
   * Get all enterprises (System Admin Only)
   * @returns {Promise<Array>} Array of enterprises
   */
  async getAllEnterprises() {
    const log = createLogger('AdminService.getAllEnterprises');
    
    const response = await this.get('/enterprises');
    
    
    // Try multiple possible response structures
    let enterprises = [];
    if (response.data?.enterprises) {
      enterprises = response.data.enterprises;
    } else if (response.enterprises) {
      enterprises = response.enterprises;
    } else if (Array.isArray(response.data)) {
      enterprises = response.data;
    } else if (Array.isArray(response)) {
      enterprises = response;
    }
    
    
    if (!Array.isArray(enterprises)) {
      log.error('AdminService.getAllEnterprises: enterprises is not an array!', enterprises);
      return [];
    }
    
    const mappedEnterprises = enterprises.map((enterprise, index) => {
      log.debug(`AdminService.getAllEnterprises: Mapping enterprise ${index}:`, enterprise);
      return new Enterprise(enterprise);
    });
    log.debug('AdminService.getAllEnterprises: Mapped enterprises:', mappedEnterprises);
    
    return mappedEnterprises;
  }

  /**
   * Update enterprise (System Admin Only)
   * @param {string} enterpriseId - Enterprise ID
   * @param {Object} updates - Enterprise updates
   * @returns {Promise<Enterprise>} Updated enterprise
   */
  async updateEnterprise(enterpriseId, updates) {
    const log = createLogger('AdminService.updateEnterprise');
    const response = await this.put(`/enterprises/${enterpriseId}`, updates);
    const enterprise = response.data?.enterprise || response.enterprise;
    return new Enterprise(enterprise);
  }

  async getPromptPreview(reportTypeOrPayload) {
    const log = createLogger('AdminService.getPromptPreview');
    // Support calling with either a string reportType or an object payload
    let payload = {};
    if (typeof reportTypeOrPayload === 'string') {
      payload.reportType = reportTypeOrPayload;
    } else if (reportTypeOrPayload && typeof reportTypeOrPayload === 'object') {
      const { reportType, isEnterprise } = reportTypeOrPayload;
      if (reportType) payload.reportType = reportType;
      if (typeof isEnterprise === 'boolean') payload.isEnterprise = isEnterprise;
    }

    const response = await this.post('/admin/preview-prompts', payload);
    log.debug(response);
    return response.data;
  }

  /**
   * Create Default Example (Admin-only)
   * Endpoint: POST /api/default-examples
   * @param {Object} data
   * @param {string} data.reportType - required
   * @param {string} data.transcription - required
   * @param {string} data.correctedContent - required
   * @param {number} [data.orderIndex] - optional
   * @param {string} [data.specialty] - optional (defaults to userProfile.specialty or 'cardiologie')
   * @returns {Promise<Object>} created example payload
   */
  async createDefaultExample(data) {
    const log = createLogger('AdminService.createDefaultExample');
    const payload = { ...data };
    return this.post('/default-examples', payload);
  }

  async deleteEnterprise(enterpriseId) {
    return this.delete(`/enterprises/${enterpriseId}`);
  }

  /**
   * Broadcast custom email to all verified users
   * @param {string} subject - Email subject
   * @param {string} htmlContent - HTML email content
   * @param {string} textContent - Plain text email content
   * @returns {Promise<Object>} Broadcast results with totalUsers, sentCount, failedCount, errors
   */
  async broadcastEmail(subject, htmlContent, textContent) {
    const log = createLogger('AdminService.broadcastEmail');
    const response = await this.post('/admin/broadcast-email', {
      subject,
      htmlContent,
      textContent
    });
    log.debug('Broadcast email response:', response);
    return response.data || response;
  }

  /**
   * Broadcast pre-defined technical issue notification to all verified users
   * @returns {Promise<Object>} Broadcast results with totalUsers, sentCount, failedCount, errors
   */
  async broadcastTechnicalIssue() {
    const log = createLogger('AdminService.broadcastTechnicalIssue');
    const response = await this.post('/admin/broadcast-technical-issue', {});
    log.debug('Technical issue broadcast response:', response);
    return response.data || response;
  }

  /**
   * Broadcast pre-defined issue resolved notification to all verified users
   * @returns {Promise<Object>} Broadcast results with totalUsers, sentCount, failedCount, errors
   */
  async broadcastIssueResolved() {
    const log = createLogger('AdminService.broadcastIssueResolved');
    const response = await this.post('/admin/broadcast-issue-resolved', {});
    log.debug('Issue resolved broadcast response:', response);
    return response.data || response;
  }
}

export default AdminService;
